import React, {useMemo, useState} from "react";
import {Box, Flex, Text, Badge, Stack, Alert, AlertIcon, Button, Checkbox} from "@chakra-ui/react";
import {useQuery} from "@tanstack/react-query";
import {fetchDependentBilling, fetchDependentPlans} from "../../api/dependents";
import {PlanList} from "../Account/Plans/PlansView";
import {PatientPlansResponseSchema} from "../Account/Plans/types";
import {useDependentSection, SECTION_STATES} from "./useDependentSection";
import DependentShell, {EmptyState} from "./DependentShell";
import {formatDate} from "./format";
import {formatMoneyCents} from "../ui/Money";
import {PayDrawer} from "../Account/Billing/PayDrawer";
import {billingStatePill} from "../Account/Billing/states";

// A dependent's account, seen by a guardian.
//
// No payment method appears on this screen at any grade, and that is structural
// rather than a setting: a guardian's cards live on the GUARDIAN's own Stripe
// customer, so one guardian's card is not on the dependent's record for another
// guardian to find. Paying for a dependent charges the payer's own method and
// applies it to the dependent's charges.
//
// The drawer below is the SAME component the guardian's own Account uses. It
// takes one extra argument — whose charges these are — and everything else is
// identical by construction: the same targets, the same Stripe Elements flow,
// the same batch engine. Two payment screens would be two places for a
// minimum, a rounding rule or a 3DS redirect to drift apart.

export default function DependentBilling({state}) {
  const section = useDependentSection({state, section: "billing", load: fetchDependentBilling});
  const data = section.data;
  const items = data?.items || [];
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [paying, setPaying] = useState(false);

  const auth = useMemo(() => ({jwt: state?.jwt}), [state?.jwt]);
  // What can be paid from here now — the same figure the patient's own Account
  // offers; never a debt a payment plan collects (audit C37).
  const payable = items.filter((item) => (item.amounts?.payableNowCents || 0) > 0);
  const canPay = data?.canPay === true && Boolean(data?.paymentConfig?.stripePublicKey);

  const selectedTotal = payable
    .filter((item) => selectedIds.has(item.itemId))
    .reduce((sum, item) => sum + (item.amounts?.payableNowCents || 0), 0);

  const toggle = (itemId) => {
    setSelectedIds((previous) => {
      const next = new Set(previous);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  return (
    <DependentShell
      title="Account"
      subtitle={`${section.displayName}'s billing`}
      displayName={section.displayName}
      accessLevel={section.dependent?.accessLevel}
      status={section.status}
      error={section.error}
      onRetry={section.reload}
      onLeave={section.leave}
      actions={
        canPay &&
        section.status === SECTION_STATES.READY &&
        payable.length > 0 && (
          <Button
            size="sm"
            colorScheme="orange"
            isDisabled={selectedIds.size === 0}
            onClick={() => setPaying(true)}
          >
            {selectedIds.size === 0
              ? "Select what to pay"
              : `Pay ${formatMoneyCents(selectedTotal)}`}
          </Button>
        )
      }
    >
      {section.status === SECTION_STATES.READY && data && (
        <>
          <Box maxW="320px" mb={5}>
            <Total
              label="To pay now"
              value={formatMoneyCents(data.totals?.payableNowCents)}
              emphasis
            />
            {data.totals?.onPaymentPlan?.itemCount > 0 && (
              <Text fontSize="sm" color="gray.600" mt={2}>
                {formatMoneyCents(data.totals.onPaymentPlan.outstandingCents)} on a payment plan
                {data.totals.onPaymentPlan.nextInstallmentCents > 0 &&
                data.totals.onPaymentPlan.nextChargeAt
                  ? ` — next ${formatMoneyCents(
                      data.totals.onPaymentPlan.nextInstallmentCents
                    )} on ${formatDate(data.totals.onPaymentPlan.nextChargeAt)}`
                  : ""}
              </Text>
            )}
          </Box>

          {items.length === 0 ? (
            <EmptyState heading="Nothing outstanding" body="There is nothing to pay on this record right now." />
          ) : (
            <Stack spacing={3}>
              {items.map((item) => {
                const outstanding = item.amounts?.outstandingCents || 0;
                const selectable = canPay && (item.amounts?.payableNowCents || 0) > 0;
                const pill = billingStatePill(item.state);
                return (
                  <Flex
                    key={item.itemId}
                    border="1px solid"
                    borderColor={selectedIds.has(item.itemId) ? "orange.300" : "gray.200"}
                    borderRadius="10px"
                    p={4}
                    bg="white"
                    justify="space-between"
                    align={{base: "flex-start", md: "center"}}
                    direction={{base: "column", md: "row"}}
                    gap={3}
                  >
                    <Flex minW={0} align="center" gap={3}>
                      {selectable && (
                        <Checkbox
                          colorScheme="orange"
                          isChecked={selectedIds.has(item.itemId)}
                          onChange={() => toggle(item.itemId)}
                          aria-label={`Pay ${item.title || "charge"}`}
                        />
                      )}
                      <Box minW={0}>
                        <Text fontWeight="600" color="#1a1f36">
                          {item.title || "Charge"}
                        </Text>
                        <Text fontSize="sm" color="gray.600">
                          {[formatDate(item.occurredAt), item.subtitle].filter(Boolean).join(" · ")}
                        </Text>
                      </Box>
                    </Flex>
                    <Flex align="center" gap={3} flexShrink={0}>
                      {item.state && (
                        <Badge
                          colorScheme={item.state === "paid" ? "green" : "orange"}
                          fontSize="10px"
                          textTransform="none"
                          borderRadius="full"
                          px={2}
                        >
                          {pill.label}
                        </Badge>
                      )}
                      <Text
                        fontWeight="700"
                        color="#1a1f36"
                        sx={{fontVariantNumeric: "tabular-nums"}}
                      >
                        {formatMoneyCents(outstanding)}
                      </Text>
                    </Flex>
                  </Flex>
                );
              })}
            </Stack>
          )}

          {!data.canPay && items.length > 0 && (
            <Alert status="info" borderRadius="10px" mt={5} fontSize="sm">
              <AlertIcon />
              You can see this account but your clinic has not given you permission to pay on it.
            </Alert>
          )}

          {data.canPay && !data.paymentConfig?.stripePublicKey && items.length > 0 && (
            <Alert status="warning" borderRadius="10px" mt={5} fontSize="sm">
              <AlertIcon />
              Your clinic does not take online payments yet. Please contact the front desk to settle
              this account.
            </Alert>
          )}

          <DependentPlans state={state} dependentPatientId={section.dependentPatientId} />

          <Text fontSize="xs" color="gray.500" mt={6}>
            Payment methods are never shared between people. Anything you pay here is charged to
            your own saved method, and no other guardian can see it.
          </Text>

          {canPay && (
            <PayDrawer
              isOpen={paying}
              onClose={() => {
                setPaying(false);
                setSelectedIds(new Set());
                section.reload();
              }}
              items={payable}
              selectedIds={selectedIds}
              auth={auth}
              stripePublicKey={data.paymentConfig.stripePublicKey}
              locationId={data.paymentConfig.locationId}
              dependentPatientId={section.dependentPatientId}
              subjectName={section.displayName}
            />
          )}
        </>
      )}
    </DependentShell>
  );
}

function Total({label, value, emphasis}) {
  return (
    <Box
      border="1px solid"
      borderColor={emphasis ? "orange.200" : "gray.200"}
      bg={emphasis ? "orange.50" : "white"}
      borderRadius="12px"
      p={4}
    >
      <Text fontSize="11px" letterSpacing="0.04em" textTransform="uppercase" color="gray.500">
        {label}
      </Text>
      <Text
        fontSize="22px"
        fontWeight="700"
        color="#1a1f36"
        sx={{fontVariantNumeric: "tabular-nums"}}
      >
        {value}
      </Text>
    </Box>
  );
}

// The dependent's memberships and packages, under their billing: shown only
// when they hold some. Read-only — the same cards the patient sees of their own.
function DependentPlans({state, dependentPatientId}) {
  const query = useQuery({
    queryKey: ["dependent-plans", dependentPatientId],
    queryFn: async () =>
      PatientPlansResponseSchema.parse({success: true, ...(await fetchDependentPlans(state, dependentPatientId))}),
    enabled: Boolean(dependentPatientId && state?.jwt),
    staleTime: 60_000,
  });
  const plans = query.data?.plans || [];
  if (plans.length === 0) return null;
  return (
    <Box mt={8}>
      <Text fontSize="sm" fontWeight="600" color="#1a1f36" mb={3}>
        Memberships &amp; packages
      </Text>
      <PlanList plans={plans} />
    </Box>
  );
}
